import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { StorageItemKeyEnum } from '../enums/StorageItemKeyEnum';
import type File from '../interfaces/File';
import type FilePermissions from '../interfaces/FilePermissions';
import { FileService } from '../services/fileService';
import { UserService } from '../services/userService';
import { getStorageItem, storeStorageItem } from '../utils/storage';

import { App } from 'antd';
import type { OTPRef } from 'antd/lib/input/OTP';
import { AiClassificationStatusEnum } from '../enums/AiClassificationStatusEnum';
import { ApiErrorEnum } from '../enums/ApiErrorEnum';
import { FileSalesStatusEnum } from '../enums/FileSalesStatusEnum';
import { FilesTableEnum } from '../enums/FilesTableEnum';
import type FileDetail from '../interfaces/FileDetail';
import { useAuth } from './useAuth';
import { useBranding } from './useBranding';
import useOpenFile from './useOpenFile';

const TIMER_DURATION = 30;
const SUBMIT_RATE_LIMIT_WINDOW_MS = 2000; // 2s, la finestra temporale per contare i submit
const SUBMIT_MAX_ATTEMPTS_IN_WINDOW = 3; // max 3 submit in finestra
const SUBMIT_BLOCK_DURATION = 30; // 30s di blocco dopo abuso

const userService = new UserService();
const fileService = new FileService();

interface FormState {
  email?: string;
  code?: string;
}

function useLogin() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { notification } = App.useApp();

  const { fileId } = useParams<{ fileId: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDummyFileSet, setIsDummyFileSet] = useState<boolean>(false);
  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);
  const [isLoadingOverlayVisible, setIsLoadingOverlayVisible] = useState<boolean>(true);
  const {
    login,
    isLoggedIn,
    authenticatedUser,
    redirectLoggedInUser,
    handleSocialLogin,
    deviceInfo,
  } = useAuth();
  const { openFileViewer } = useOpenFile();
  // Temporary use of the files hook to open the file viewer, next we will use the useOpenFile hook
  const [form, setForm] = useState<FormState>({
    code: '',
    email: authenticatedUser?.email || '',
  });

  const [isOtpRequested, setIsOtpRequested] = useState<boolean>(false);
  const [otpTimer, setOtpTimer] = useState<number | null>(null);
  const [selectedFile, setSelectedFile] = useState<any | null>(null);
  const memoizedRedirectLoggedInUser = useCallback(redirectLoggedInUser, []);
  const otpInputRef = useRef<OTPRef>(null);

  const submitTimestamps = useRef<number[]>([]);
  const [isSubmitBlocked, setIsSubmitBlocked] = useState(false);
  const [submitBlockTimer, setSubmitBlockTimer] = useState(0);

  const { brandingData } = useBranding();

  /**
   * Handles the OTP (One Time Password) request process.
   *
   * Prevents multiple OTP requests by checking if a timer is already running.
   * If no timer is active, it requests a new OTP and performs several state update actions:
   * - Sets an OTP request flag to true.
   * - Initializes an OTP timer with a predefined duration.
   * - Stores the OTP timer duration in the local storage.
   * - Resets the 'code' field in the form state.
   */
  const handleRequestOtp = async () => {
    if (otpTimer !== null) return;

    await requestOtp().then(() => {
      setIsOtpRequested(true);
      setOtpTimer(TIMER_DURATION);
      storeStorageItem('localStorage', StorageItemKeyEnum.otpRequestTimer, TIMER_DURATION);
      setForm((prevForm) => ({
        ...prevForm,
        code: '',
      }));
      setTimeout(() => {
        otpInputRef.current?.focus();
      }, 200);
    });
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  const getProviderKeyByEmail = (email: string) => {
    const domain = email.split('@')[1];
    if (brandingData && brandingData.providers && domain) {
      for (const [key, value] of Object.entries(brandingData.providers)) {
        if (value.isEnabled) {
          const domainsList = value.domains
            .split(',')
            .map((d) => d.trim())
            .filter(Boolean); // Rimuove stringhe vuote

          if (domainsList.includes(domain)) {
            return key;
          }
        }
      }
    }

    return null;
  };

  /**
   * Asynchronously requests an OTP (One-Time Password) for the user.
   *
   * The function sets the loading state to true, constructs a payload with device and form information,
   * and sends a request for an OTP through the user service. Depending on the response, it either sets
   * the OTP requested state to true or handles errors by creating notifications for specific error statuses.
   *
   * @function requestOtp
   * @async
   */
  const requestOtp = async () => {
    const provider = form.email && getProviderKeyByEmail(form.email);
    if (provider) {
      handleSocialLogin(provider);
      return;
    }

    setIsLoading(true);
    const payload = {
      culture: deviceInfo?.culture,
      username: form.email,
      offSet: deviceInfo?.offSetClientTime,
      tenantUrl: window.location.origin,
    };

    await userService
      .requestUserOtp(payload)
      .then(() => {
        setIsOtpRequested(true);
        setTimeout(() => {
          otpInputRef.current?.focus();
        }, 200);
      })
      .catch((e) => {
        if (e.response === 'USER_BLOCKED') {
          notification.error({
            message: t('errors.authenticationUserBlocked'),
          });
          return;
        }

        if (e.response === 'USER_EXPIRED') {
          notification.error({
            message: t('errors.authenticationUserExpired'),
          });
          return;
        }

        if (e?.response?.name === 'TENANT_NOT_FOUND') {
          notification.error({
            message: e.response.value,
          });
          return;
        }

        if (e.status === 404) {
          notification.error({
            message: t('errors.emailNotFound', {
              email: form.email,
            }),
          });
          return;
        }

        if (e.status === 429) {
          notification.error({
            message: t('errors.otpRequestLimitExceeded'),
          });
          return;
        }

        notification.error({
          message: t('errors.genericError'),
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Authenticates the user by sending a login request with the provided payload.
   *
   * This function will set the loading state to true, build a payload with the
   * code, email, and tenant URL, and then attempt to log in the user using the
   * userService. If the login is successful, the login function will be called
   * with the response and a flag set to true. If there is an error (e.g., the OTP
   * has expired), a notification will be created with a danger status. Regardless
   * of success or failure, the loading state will be set to false at the end.
   *
   * @function
   * @async
   * @returns {Promise<void>} A promise that resolves when the authentication process is complete.
   */
  const authenticate = async (code: string): Promise<void> => {
    setIsLoading(true);
    const payload = {
      code: code,
      username: form.email,
      tenantUrl: window.location.origin,
    };

    await userService
      .login(payload)
      .then(async (res) => {
        if (selectedFile?.fileProtected?.fileId) {
          await login(res.token, false);
        } else {
          await login(res.token, true);
        }
      })
      .catch((error) => {
        if (error?.response?.name === 'OTP_WRONG') {
          notification.error({
            message: t('errors.otpWrong'),
          });
        } else {
          notification.error({
            message: t('errors.otpExpired'),
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Redirects the user based on the properties of the provided file.
   *
   * @param fileInfo
   * @param fileDetail
   * @param userId
   */
  const checkFileRedirect = (
    fileInfo: FilePermissions,
    fileDetail: FileDetail,
    userId: string | number | undefined | null,
  ) => {
    if (fileInfo.fileProtected.fileExtension === '') {
      if (userId && userId.toString() === fileInfo?.fileProtected?.userId?.toString()) {
        navigate(`/files/${FilesTableEnum.Personal}/folder/${fileInfo.fileProtected.uid}`);
      } else {
        navigate(`/files/${FilesTableEnum.Received}/folder/${fileInfo.fileProtected.uid}`);
      }
      return;
    } else {
      const dummyFile: File = {
        aiOperationStatus: AiClassificationStatusEnum.None,
        id: fileInfo.fileProtected.uid,
        cufi: fileInfo.fileProtected.fileId,
        fileName: fileInfo.fileProtected.originaleFileName,
        fileExtension: fileInfo.fileProtected.fileExtension,
        thumbnail: '',
        isReadOnly: false,
        isFavourite: false,
        isArchived: false,
        isDeleted: false,
        isShared: false,
        isUnRead: false,
        isQuickShare: false,
        lastModifiedAt: fileInfo.fileProtected.lastModifiedAt,
        fileSize: fileInfo.fileProtected.fileSize,
        isFolder: false,
        parentId: null,
        ownerId: fileInfo.fileProtected.userId,
        tags: fileDetail.tags.map((tag) => ({
          id: tag.id,
          readOnly: false,
        })),
        path: [
          {
            uid: fileInfo.fileProtected.uid,
            fileId: fileInfo.fileProtected.fileId,
            title: fileInfo.fileProtected.originaleFileName,
          },
        ],
        fileLimitations: [],
        fileLimitationsUsers: [],
        fileLimitationsGroups: [],
        salesStatus: FileSalesStatusEnum.None,
        isShortcut: false,
      };

      if (!openFileViewer(fileInfo, dummyFile, false)) {
        navigate(`/files/${FilesTableEnum.Personal}`);
      }
    }
  };

  /**
   * Function to request and return file data based on the provided file ID.
   *
   * This function sets the loading state to true and shows a loading overlay
   * while the file request is being processed. It then attempts to retrieve the
   * file using the file service. If the request succeeds, the response is returned.
   * If the request fails, an error notification is created and the selected file
   * is set to null. Finally, the loading state is set to false.
   *
   * @returns {Promise<*>} A promise that resolves to the response of the file request.
   * @param fileDetail
   */
  const getFilePermissions = async (fileDetail: FileDetail): Promise<null | FilePermissions> => {
    setIsLoading(true);
    setIsLoadingOverlayVisible(true);

    if (!fileDetail.uid) {
      setIsLoadingOverlayVisible(false);
      setIsLoading(false);
      return null;
    }

    return fileService
      .getFilePermissions(fileDetail.uid)
      .then((response) => {
        if (response.fileGranted.fileLimitation.accessDenied) {
          notification.error({
            message: t('errors.requestFilePermissions'),
          });
          setIsLoadingOverlayVisible(false);
          setIsLoading(false);
          return null;
        }
        return response;
      })
      .catch((error) => {
        switch (error.response) {
          case ApiErrorEnum.AntiCaptureActivated:
            navigate(`/file/protected/${fileDetail.uid}`);
            break;
          case ApiErrorEnum.DownloadRestrictionActivated:
            notification.error({
              message: t('errors.downloadRestrictionActivated'),
            });
            navigate(`/file/protected/${fileDetail.uid}`);
            break;
          default:
            // Check if file is has shortcut as extension
            if (fileDetail.isShortcut) {
              notification.error({
                message: t('errors.openShortcutFileError'),
              });
            } else {
              notification.error({
                message: t('errors.requestFilePermissions'),
              });
            }
            redirectLoggedInUser();
        }
        setSelectedFile(null);
        return null;
      })
      .finally(() => {
        setIsLoadingOverlayVisible(false);
        setIsLoading(false);
      });
  };

  /**
   * Retrieves information about a specified file.
   *
   * This asynchronous function utilizes the `fileService` to fetch file information based on a specified `fileId`.
   * It manages loading state, handles potential errors, and provides notifications for the user.
   *
   * @function
   * @name getFileDetail
   *
   * @property {string} fileId - The unique identifier of the file whose information is being retrieved.
   *
   * @returns {Promise<void>} A promise that resolves when the file information retrieval process is complete.
   */
  const getFileDetail = useCallback(
    async (requestedFileId: string | number) => {
      setIsLoading(true);
      setIsLoadingOverlayVisible(true);
      try {
        const response = await fileService.getFile(requestedFileId);
        const file = await getFilePermissions(response);
        if (file) {
          checkFileRedirect(file, response, authenticatedUser?.id);
        }
      } catch {
        notification.error({
          message: t('errors.requestFilePermissions'),
        });
        if (authenticatedUser?.id) {
          redirectLoggedInUser();
        } else {
          navigate('/login');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [fileService, t, checkFileRedirect, authenticatedUser?.id, getFilePermissions, navigate],
  );

  /**
   * Handles the form submission event.
   * Prevents the default form submission behavior, and based on the state of OTP request,
   * either initiates the request for OTP or proceeds with authentication.
   *
   * @param {FormEvent} e - The form event object.
   * @returns {Promise<void>} - A promise that resolves when the form handling logic is complete.
   */
  const onSubmit = async (): Promise<void> => {
    if (isSubmitBlocked) return;

    const now = Date.now();
    submitTimestamps.current = submitTimestamps.current
      .filter((ts) => now - ts < SUBMIT_RATE_LIMIT_WINDOW_MS)
      .concat(now);

    if (submitTimestamps.current.length >= SUBMIT_MAX_ATTEMPTS_IN_WINDOW) {
      setIsSubmitBlocked(true);
      setSubmitBlockTimer(SUBMIT_BLOCK_DURATION);
      notification.error({
        message: t('auth.loginPageTooManyAttempts') + ` ${SUBMIT_BLOCK_DURATION}s`,
      });
      return;
    }

    if (!isOtpRequested) {
      await requestOtp();
    } else {
      await authenticate(form.code || '');
    }
  };

  /**
   * A memoized boolean value that represents whether the email field in the `form` object
   * is valid.
   *
   * This variable uses a regular expression to check the validity of the email format.
   * If the email is invalid, it resets the `code` property in the `form` object to an
   * empty string.
   *
   * Dependencies:
   * - The `form.email` field.
   */
  const isEmailValid = useMemo(() => {
    if (form?.email) {
      const emailRegex = new RegExp('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$');
      const isValid = emailRegex.test(form?.email);

      if (!isValid) {
        setForm((prevForm) => ({
          ...prevForm,
          code: '',
        }));
        return false;
      }
      return true;
    } else {
      return false;
    }
  }, [form.email]);

  useEffect(() => {
    setIsOtpRequested(false);
  }, [form.email]);

  useEffect(() => {
    if (otpTimer === null) return;

    const intervalId = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev === null || prev === 0) {
          storeStorageItem('localStorage', StorageItemKeyEnum.otpRequestTimer, 0);
          return null;
        } else {
          storeStorageItem('localStorage', StorageItemKeyEnum.otpRequestTimer, prev - 1);
          return prev - 1;
        }
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [otpTimer]);

  useEffect(() => {
    const checkStoredTimer = async () => {
      const storedTimer = await getStorageItem('localStorage', StorageItemKeyEnum.otpRequestTimer);
      if (storedTimer && storedTimer > 0) {
        setOtpTimer(storedTimer);
      }
    };
    checkStoredTimer().then(() => {});
  }, []);

  useEffect(() => {
    if (fileId && !selectedFile && !isDummyFileSet) {
      setSelectedFile({
        fileProtected: {
          fileId: fileId,
          fileExtension: '',
          originaleFileName: 'File Shared',
          userId: null,
        },
      });
      setIsDummyFileSet(true);
    }

    if (isLoggedIn && authenticatedUser && !fileId) {
      redirectLoggedInUser();
    }

    // Redirect if user is already logged and there's no fileId
  }, [
    fileId,
    selectedFile,
    isLoggedIn,
    authenticatedUser,
    memoizedRedirectLoggedInUser,
    isDummyFileSet,
  ]);

  useEffect(() => {
    if (isLoggedIn && selectedFile && selectedFile.fileProtected?.fileId) {
      getFileDetail(selectedFile.fileProtected.fileId).then(() => {});
    } else {
      setIsLoadingOverlayVisible(false);
    }
  }, [isLoggedIn, selectedFile, memoizedRedirectLoggedInUser]);

  useEffect(() => {
    setIsPageLoaded(true);
  }, []);

  /**
   * Handles the Submit block timer.
   *
   * This effect manages the OTP block timer, which is used to prevent multiple OTP requests within a short period of time.
   * It sets up an interval to decrement the timer and resets the block state when the timer reaches 0.
   *
   * @function
   */
  useEffect(() => {
    if (isSubmitBlocked && submitBlockTimer > 0) {
      const interval = setInterval(() => {
        setSubmitBlockTimer((prev) => {
          if (prev <= 1) {
            setIsSubmitBlocked(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isSubmitBlocked, submitBlockTimer]);

  return {
    form,
    handleRemoveFile,
    handleRequestOtp,
    isLoading,
    isLoadingOverlayVisible,
    isLoggedIn,
    isOtpRequested,
    onSubmit,
    otpTimer,
    selectedFile,
    setForm,
    isEmailValid,
    authenticate,
    isPageLoaded,
    otpInputRef,
    t,
    isSubmitBlocked,
    submitBlockTimer,
  };
}

export default useLogin;
